import React, { useMemo, useState } from "react";
import {
    ArrowRight,
    ArrowLeft,
    CheckCircle2,
    Sparkles,
    Wand2,
    Building2,
    Target,
    Users,
    Palette,
    Layers
} from "lucide-react";

import {
    subIndustryOptionsByIndustry,
    industryOptions,
    brandStyleOptions
} from "../../data/websiteOptions";

const INDUSTRY_PRESETS = {
    "Education": {
        businessModel: "academy",
        primaryWebsiteMode: "program-first",
        primaryEntityType: "programs",
        pages: ["Home", "Programs", "Coaches", "Admissions", "Contact"],
        callToActionType: "Book Trial",
        pricePositioning: "Premium",
        contentStyleHint: "clean",
        descriptionHint: "academy, programs, coaches, admissions, training outcomes"
    },
    "Academy / Coaching": {
        businessModel: "academy",
        primaryWebsiteMode: "program-first",
        primaryEntityType: "programs",
        pages: ["Home", "Programs", "Coaches", "About", "Contact"],
        callToActionType: "Enquiry",
        pricePositioning: "Premium",
        contentStyleHint: "clean",
        descriptionHint: "academy, programs, admissions, coaches, training outcomes"
    },
    "Real Estate": {
        businessModel: "service",
        primaryWebsiteMode: "lead-first",
        primaryEntityType: "listings",
        pages: ["Home", "Properties", "Services", "About", "Contact"],
        callToActionType: "Book Consultation",
        pricePositioning: "Premium",
        contentStyleHint: "premium",
        descriptionHint: "properties, buying, selling, leasing, advisory, local expertise"
    },
    "Ecommerce": {
        businessModel: "ecommerce",
        primaryWebsiteMode: "product-first",
        primaryEntityType: "products",
        pages: ["Home", "Shop", "Collections", "About", "Contact"],
        callToActionType: "Shop Now",
        pricePositioning: "Mid Range",
        contentStyleHint: "modern",
        descriptionHint: "products, collections, customers, offers, shopping experience"
    },
    "Ecommerce / Store": {
        businessModel: "ecommerce",
        primaryWebsiteMode: "product-first",
        primaryEntityType: "products",
        pages: ["Home", "Shop", "Collections", "About", "Contact"],
        callToActionType: "Shop Now",
        pricePositioning: "Mid Range",
        contentStyleHint: "modern",
        descriptionHint: "products, collections, customers, offers, shopping experience"
    },
    "Healthcare": {
        businessModel: "service",
        primaryWebsiteMode: "trust-first",
        primaryEntityType: "services",
        pages: ["Home", "Services", "Doctors", "About", "Contact"],
        callToActionType: "Book Appointment",
        pricePositioning: "Premium",
        contentStyleHint: "clean",
        descriptionHint: "clinic, treatments, doctors, appointments, patient trust"
    },
    "Healthcare / Clinic": {
        businessModel: "service",
        primaryWebsiteMode: "trust-first",
        primaryEntityType: "services",
        pages: ["Home", "Services", "Doctors", "About", "Contact"],
        callToActionType: "Book Appointment",
        pricePositioning: "Premium",
        contentStyleHint: "clean",
        descriptionHint: "clinic, treatments, doctors, appointments, patient trust"
    },
    "Legal": {
        businessModel: "service",
        primaryWebsiteMode: "authority-first",
        primaryEntityType: "services",
        pages: ["Home", "Practice Areas", "Team", "About", "Contact"],
        callToActionType: "Book Consultation",
        pricePositioning: "Premium",
        contentStyleHint: "premium",
        descriptionHint: "law firm, practice areas, consultation, legal expertise, trust"
    },
    "Legal / Law Firm": {
        businessModel: "service",
        primaryWebsiteMode: "authority-first",
        primaryEntityType: "services",
        pages: ["Home", "Practice Areas", "Team", "About", "Contact"],
        callToActionType: "Book Consultation",
        pricePositioning: "Premium",
        contentStyleHint: "premium",
        descriptionHint: "law firm, practice areas, consultation, legal expertise, trust"
    },
    "SaaS": {
        businessModel: "saas",
        primaryWebsiteMode: "conversion-first",
        primaryEntityType: "features",
        pages: ["Home", "Features", "Pricing", "About", "Contact"],
        callToActionType: "Start Free Trial",
        pricePositioning: "Premium",
        contentStyleHint: "modern",
        descriptionHint: "software, features, product benefits, onboarding, conversion"
    },
    "SaaS / Tech": {
        businessModel: "saas",
        primaryWebsiteMode: "conversion-first",
        primaryEntityType: "features",
        pages: ["Home", "Features", "Pricing", "About", "Contact"],
        callToActionType: "Start Free Trial",
        pricePositioning: "Premium",
        contentStyleHint: "modern",
        descriptionHint: "software, features, product benefits, onboarding, conversion"
    },
    "Fitness": {
        businessModel: "academy",
        primaryWebsiteMode: "program-first",
        primaryEntityType: "programs",
        pages: ["Home", "Programs", "Trainers", "Contact"],
        callToActionType: "Book Trial",
        pricePositioning: "Premium",
        contentStyleHint: "bold",
        descriptionHint: "fitness programs, trainers, memberships, transformation, trial classes"
    },
    "Beauty": {
        businessModel: "service",
        primaryWebsiteMode: "booking-first",
        primaryEntityType: "services",
        pages: ["Home", "Services", "Gallery", "About", "Contact"],
        callToActionType: "Book Appointment",
        pricePositioning: "Premium",
        contentStyleHint: "luxury",
        descriptionHint: "salon, beauty services, appointments, premium experience, gallery"
    },
    "Restaurant": {
        businessModel: "service",
        primaryWebsiteMode: "conversion-first",
        primaryEntityType: "services",
        pages: ["Home", "Menu", "Gallery", "About", "Contact"],
        callToActionType: "Reserve Table",
        pricePositioning: "Mid Range",
        contentStyleHint: "warm",
        descriptionHint: "restaurant, menu, dining, reservations, ambience, food experience"
    }
};

const STYLE_OPTIONS = [
    { key: "Premium", label: "Premium", helper: "High-trust, polished and conversion-focused" },
    { key: "Modern", label: "Modern", helper: "Clean SaaS-style layout with sharp sections" },
    { key: "Luxury", label: "Luxury", helper: "Elegant spacing, premium copy, refined visuals" },
    { key: "Bold", label: "Bold", helper: "Strong hero, punchy sections, energetic visual tone" },
    { key: "Minimal", label: "Minimal", helper: "Simple, clean and lightweight presentation" }
];

const AUDIENCE_OPTIONS = [
    "Local customers",
    "Premium clients",
    "Parents / students",
    "Home buyers",
    "Patients",
    "Business owners",
    "Online shoppers",
    "Startups / teams"
];

const INDUSTRY_GUIDED_OPTIONS = {
    "Fitness": {
        goalOptions: [
            "Book Trial",
            "View Memberships",
            "Talk to Trainer",
            "Join Program",
            "Request Fitness Consultation"
        ],
        audienceOptions: [
            "Fitness seekers",
            "Local customers",
            "Beginners",
            "Personal training clients",
            "Transformation-focused members"
        ],
        coreServicesPlaceholder:
            "e.g. Personal training, strength training, cardio equipment, memberships, trial sessions"
    },
    "Healthcare": {
        goalOptions: [
            "Book Appointment",
            "Call Clinic",
            "Meet Doctors",
            "Request Consultation"
        ],
        audienceOptions: [
            "Patients",
            "Families",
            "Local customers",
            "Health seekers"
        ],
        coreServicesPlaceholder:
            "e.g. Doctor consultations, treatments, health checkups, patient support"
    },
    "Real Estate": {
        goalOptions: [
            "Schedule Site Visit",
            "View Listings",
            "Talk to Advisor",
            "Request Callback"
        ],
        audienceOptions: [
            "Home buyers",
            "Property investors",
            "Sellers",
            "Tenants"
        ],
        coreServicesPlaceholder:
            "e.g. Property listings, buying advisory, selling support, rental assistance, site visits"
    },
    "Education": {
        goalOptions: [
            "Book Trial",
            "View Programs",
            "Apply for Admission",
            "Talk to Coach"
        ],
        audienceOptions: [
            "Parents / students",
            "Kids 6-16",
            "Beginners",
            "Advanced learners"
        ],
        coreServicesPlaceholder:
            "e.g. Football training, weekend batches, beginner programs, advanced coaching"
    },
    "Ecommerce / Store": {
        goalOptions: [
            "Shop Now",
            "View Collections",
            "Explore Offers",
            "Contact Store"
        ],
        audienceOptions: [
            "Online shoppers",
            "Fashion buyers",
            "Deal seekers",
            "Premium customers"
        ],
        coreServicesPlaceholder:
            "e.g. Products, collections, offers, online shopping, customer support"
    },
    "Legal": {
        goalOptions: [
            "Book Consultation",
            "View Practice Areas",
            "Talk to Legal Expert",
            "Request Case Review"
        ],
        audienceOptions: [
            "Business owners",
            "Individuals",
            "Companies",
            "Legal clients"
        ],
        coreServicesPlaceholder:
            "e.g. Legal advisory, consultations, case evaluation, practice areas"
    },
    "SaaS / Tech": {
        goalOptions: [
            "Start Free Trial",
            "Book Demo",
            "View Features",
            "Contact Sales"
        ],
        audienceOptions: [
            "Startups / teams",
            "Business owners",
            "Product teams",
            "Growing companies"
        ],
        coreServicesPlaceholder:
            "e.g. Product features, pricing, onboarding, team productivity, integrations"
    },
    "Restaurant": {
        goalOptions: [
            "View Menu",
            "Order Now",
            "Book Table",
            "Call Restaurant"
        ],
        audienceOptions: [
            "Local customers",
            "Food lovers",
            "Families",
            "Office groups"
        ],
        coreServicesPlaceholder:
            "e.g. Menu, table booking, online orders, offers, location details"
    },
    "Beauty": {
        goalOptions: [
            "Book Appointment",
            "View Services",
            "Explore Packages",
            "Call Studio"
        ],
        audienceOptions: [
            "Local customers",
            "Premium clients",
            "Beauty seekers",
            "Brides"
        ],
        coreServicesPlaceholder:
            "e.g. Beauty services, hair styling, makeup, spa packages, appointments"
    },
    "Construction": {
        goalOptions: [
            "Request Quote",
            "View Projects",
            "Book Consultation",
            "Contact Team"
        ],
        audienceOptions: [
            "Property owners",
            "Developers",
            "Business owners",
            "Home renovators"
        ],
        coreServicesPlaceholder:
            "e.g. Construction services, project portfolio, renovation, consultation"
    }
};

function getGuidedOptions(industry) {
    return INDUSTRY_GUIDED_OPTIONS[industry] || {
        goalOptions: [
            "Generate Leads",
            "Contact Us",
            "Request Callback",
            "Book Consultation"
        ],
        audienceOptions: AUDIENCE_OPTIONS,
        coreServicesPlaceholder:
            "e.g. Services, consultation, customer support, lead capture"
    };
}

function getPreset(industry) {
    return INDUSTRY_PRESETS[industry] || {};
}

function buildDescription(form, answers) {
    const businessName = answers.businessName || form.businessName || "This business";
    const industry = answers.industry || form.industry || "business";
    const subIndustry = answers.subIndustry || form.subIndustry || "";
    const audience = answers.primaryAudience || form.primaryAudience || "customers";
    const offerings = answers.coreServices || form.coreServices || "";
    const preset = getPreset(industry);

    const parts = [
        `${businessName} is a ${subIndustry || industry} focused on serving ${audience}.`,
        offerings ? `Main offerings include ${offerings}.` : "",
        preset.descriptionHint ? `The website should highlight ${preset.descriptionHint}.` : "",
        "The experience should feel trustworthy, clear, and conversion-ready."
    ];

    return parts.filter(Boolean).join(" ");
}

function QuestionShell({ icon: Icon, eyebrow, title, subtitle, children }) {
    return (
        <div className="wg-assistant-question">
            <div className="wg-assistant-question-head">
                <div className="wg-assistant-question-icon">
                    <Icon size={20} />
                </div>
                <div>
                    <span>{eyebrow}</span>
                    <h3>{title}</h3>
                    {subtitle ? <p>{subtitle}</p> : null}
                </div>
            </div>

            {children}
        </div>
    );
}

function ChoiceGrid({ options, value, onChange }) {
    return (
        <div className="wg-assistant-choice-grid">
            {options.map((option) => {
                const item = typeof option === "string"
                    ? { key: option, label: option }
                    : option;

                return (
                    <button
                        key={item.key || item.label}
                        type="button"
                        className={`wg-assistant-choice ${value === (item.key || item.label) ? "active" : ""}`}
                        onClick={() => onChange(item.key || item.label)}
                    >
                        <strong>{item.label}</strong>
                        {item.helper ? <small>{item.helper}</small> : null}
                    </button>
                );
            })}
        </div>
    );
}

export default function AiGeneratorAssistant({
    form,
    updateField,
    onAutofill,
    autofilling,
    onUseManual,
    onComplete
}) {
    const [index, setIndex] = useState(0);
    const [answers, setAnswers] = useState({
        businessName: form.businessName || "",
        industry: form.industry || "",
        subIndustry: form.subIndustry || "",
        goalOfWebsite: form.goalOfWebsite || "",
        primaryAudience: form.primaryAudience || "",
        coreServices: form.coreServices || "",
        brandStyle: form.brandStyle || "",
        location: form.location || ""
    });

    const industry = answers.industry || form.industry || "";
    const preset = useMemo(() => getPreset(industry), [industry]);
    const guidedOptions = useMemo(() => getGuidedOptions(industry), [industry]);

    const subIndustryOptions = useMemo(() => {
        return subIndustryOptionsByIndustry[industry] || [];
    }, [industry]);

    const normalizedIndustryOptions = useMemo(() => {
        const base = Array.isArray(industryOptions) && industryOptions.length > 0
            ? industryOptions
            : Object.keys(INDUSTRY_PRESETS);

        return [...new Set(base)];
    }, []);

    const setAnswer = (key, value) => {
        setAnswers((prev) => ({
            ...prev,
            [key]: value
        }));

        updateField(key, value);

        if (key === "industry") {
            const nextPreset = getPreset(value);
            const nextGuidedOptions = getGuidedOptions(value);
            const defaultGoal = nextGuidedOptions.goalOptions?.[0] || nextPreset.callToActionType || "";

            updateField("subIndustry", "");
            updateField("businessModel", nextPreset.businessModel || "");
            updateField("primaryWebsiteMode", nextPreset.primaryWebsiteMode || "");
            updateField("primaryEntityType", nextPreset.primaryEntityType || "");
            updateField("callToActionType", defaultGoal);
            updateField("goalOfWebsite", defaultGoal);
            updateField("pricePositioning", nextPreset.pricePositioning || "");
            updateField("contentStyleHint", nextPreset.contentStyleHint || "");

            if (Array.isArray(nextPreset.pages)) {
                updateField("pages", nextPreset.pages);
            }

            setAnswers((prev) => ({
                ...prev,
                industry: value,
                subIndustry: "",
                goalOfWebsite: defaultGoal,
                primaryAudience: nextGuidedOptions.audienceOptions?.[0] || prev.primaryAudience || ""
            }));

            if (nextGuidedOptions.audienceOptions?.[0]) {
                updateField("primaryAudience", nextGuidedOptions.audienceOptions[0]);
            }
        }
    };

    const applyFinalPlan = async () => {
        const finalDescription =
            form.description?.trim() ||
            buildDescription(form, answers);

        updateField("businessName", answers.businessName || form.businessName || "");
        updateField("industry", answers.industry || form.industry || "");
        updateField("subIndustry", answers.subIndustry || form.subIndustry || "");
        const finalGoal =
            answers.goalOfWebsite ||
            form.goalOfWebsite ||
            preset.callToActionType ||
            guidedOptions.goalOptions?.[0] ||
            "";

        updateField("goalOfWebsite", finalGoal);
        updateField("callToActionType", finalGoal);
        updateField("primaryAudience", answers.primaryAudience || form.primaryAudience || "");
        updateField("coreServices", answers.coreServices || form.coreServices || "");
        updateField("brandStyle", answers.brandStyle || form.brandStyle || "Premium");
        updateField("location", answers.location || form.location || "");
        updateField("description", finalDescription);

        updateField("businessModel", preset.businessModel || form.businessModel || "");
        updateField("primaryWebsiteMode", preset.primaryWebsiteMode || form.primaryWebsiteMode || "");
        updateField("primaryEntityType", preset.primaryEntityType || form.primaryEntityType || "");
        
        updateField("pricePositioning", preset.pricePositioning || form.pricePositioning || "");
        updateField("contentStyleHint", preset.contentStyleHint || form.contentStyleHint || "");

        if (Array.isArray(preset.pages) && preset.pages.length > 0) {
            updateField("pages", preset.pages);
        }

        if (onComplete) {
            onComplete();
        }
    };

    const questions = [
        {
            key: "businessName",
            render: () => (
                <QuestionShell
                    icon={Sparkles}
                    eyebrow="Step 1"
                    title="What is your business name?"
                    subtitle="This will become the base for your website brand and CRM project."
                >
                    <input
                        className="wg-assistant-input"
                        value={answers.businessName}
                        onChange={(e) => setAnswer("businessName", e.target.value)}
                        placeholder="e.g. Urban Realty, Elite Sports Academy, CareNest Clinic"
                        autoFocus
                    />
                </QuestionShell>
            ),
            isValid: () => !!answers.businessName?.trim()
        },
        {
            key: "industry",
            render: () => (
                <QuestionShell
                    icon={Building2}
                    eyebrow="Step 2"
                    title="Choose your industry"
                    subtitle="Nexus uses this to select the right page structure, modules, and visual pattern."
                >
                    <ChoiceGrid
                        options={normalizedIndustryOptions}
                        value={answers.industry}
                        onChange={(value) => setAnswer("industry", value)}
                    />
                </QuestionShell>
            ),
            isValid: () => !!answers.industry?.trim()
        },
        {
            key: "subIndustry",
            render: () => (
                <QuestionShell
                    icon={Layers}
                    eyebrow="Step 3"
                    title="What type of business is it?"
                    subtitle="Pick a sub-industry or type your own if it is not listed."
                >
                    {subIndustryOptions.length > 0 ? (
                        <ChoiceGrid
                            options={subIndustryOptions}
                            value={answers.subIndustry}
                            onChange={(value) => setAnswer("subIndustry", value)}
                        />
                    ) : null}

                    <input
                        className="wg-assistant-input"
                        value={answers.subIndustry}
                        onChange={(e) => setAnswer("subIndustry", e.target.value)}
                        placeholder="Or type custom sub-industry"
                    />
                </QuestionShell>
            ),
            isValid: () => !!answers.subIndustry?.trim()
        },
        {
            key: "goalOfWebsite",
            render: () => (
                <QuestionShell
                    icon={Target}
                    eyebrow="Step 4"
                    title="What is the main goal of this website?"
                    subtitle="This helps AI decide the CTA, page flow, and conversion sections."
                >
                    <ChoiceGrid
                        options={guidedOptions.goalOptions}
                        value={answers.goalOfWebsite}
                        onChange={(value) => {
                            setAnswer("goalOfWebsite", value);
                            updateField("callToActionType", value);
                        }}
                    />

                    <div className="wg-assistant-mini-note">
                        Showing CTA options for {industry || "your selected industry"} only.
                    </div>

                    {industry ? (
                        <div className="wg-assistant-lock-note">
                            Industry locked: {industry}. CTA choices are filtered to avoid wrong website type.
                        </div>
                    ) : null}
                </QuestionShell>
            ),
            isValid: () => !!answers.goalOfWebsite?.trim()
        },
        {
            key: "primaryAudience",
            render: () => (
                <QuestionShell
                    icon={Users}
                    eyebrow="Step 5"
                    title="Who should this website speak to?"
                    subtitle="Choose a common audience or type your own."
                >
                    <ChoiceGrid
                        options={guidedOptions.audienceOptions}
                        value={answers.primaryAudience}
                        onChange={(value) => setAnswer("primaryAudience", value)}
                    />

                    <input
                        className="wg-assistant-input"
                        value={answers.primaryAudience}
                        onChange={(e) => setAnswer("primaryAudience", e.target.value)}
                        placeholder="e.g. Parents looking for sports training, premium home buyers, clinic patients"
                    />
                </QuestionShell>
            ),
            isValid: () => !!answers.primaryAudience?.trim()
        },
        {
            key: "coreServices",
            render: () => (
                <QuestionShell
                    icon={Wand2}
                    eyebrow="Step 6"
                    title="What do you offer?"
                    subtitle="Add services, products, programs, listings, treatments, or features."
                >
                    <textarea
                        className="wg-assistant-textarea"
                        value={answers.coreServices}
                        onChange={(e) => setAnswer("coreServices", e.target.value)}
                        rows={5}
                        placeholder={guidedOptions.coreServicesPlaceholder}
                    />
                </QuestionShell>
            ),
            isValid: () => !!answers.coreServices?.trim()
        },
        {
            key: "brandStyle",
            render: () => (
                <QuestionShell
                    icon={Palette}
                    eyebrow="Step 7"
                    title="Choose the visual direction"
                    subtitle="AI will still refine this using your industry and business type."
                >
                    <ChoiceGrid
                        options={STYLE_OPTIONS}
                        value={answers.brandStyle}
                        onChange={(value) => setAnswer("brandStyle", value)}
                    />

                    {Array.isArray(brandStyleOptions) && brandStyleOptions.length > 0 ? (
                        <div className="wg-assistant-mini-note">
                            You can fine-tune brand style later in manual mode.
                        </div>
                    ) : null}
                </QuestionShell>
            ),
            isValid: () => !!answers.brandStyle?.trim()
        },
        {
            key: "summary",
            render: () => (
                <QuestionShell
                    icon={CheckCircle2}
                    eyebrow="Final step"
                    title="Your AI website plan is ready"
                    subtitle="Review the plan. Nexus will fill the generator fields and prepare AI suggestions."
                >
                    <div className="wg-assistant-summary-grid">
                        <div>
                            <span>Business</span>
                            <strong>{answers.businessName || "-"}</strong>
                        </div>
                        <div>
                            <span>Industry</span>
                            <strong>{answers.industry || "-"}</strong>
                        </div>
                        <div>
                            <span>Type</span>
                            <strong>{answers.subIndustry || "-"}</strong>
                        </div>
                        <div>
                            <span>Goal</span>
                            <strong>{answers.goalOfWebsite || "-"}</strong>
                        </div>
                        <div>
                            <span>Audience</span>
                            <strong>{answers.primaryAudience || "-"}</strong>
                        </div>
                        <div>
                            <span>Style</span>
                            <strong>{answers.brandStyle || "-"}</strong>
                        </div>
                    </div>

                    <div className="wg-assistant-plan-card">
                        <strong>Recommended structure</strong>
                        <div className="wg-assistant-page-pills">
                            {(preset.pages || form.pages || ["Home", "About", "Services", "Contact"]).map((page) => (
                                <span key={page}>{page}</span>
                            ))}
                        </div>

                        <p>
                            AI will prepare strategy fields like business model, website focus,
                            entity type, CTA, page structure, and content direction.
                        </p>
                    </div>
                </QuestionShell>
            ),
            isValid: () => true
        }
    ];

    const current = questions[index];
    const isLast = index === questions.length - 1;
    const progress = Math.round(((index + 1) / questions.length) * 100);
    const completedCount = Math.max(0, index);
    const totalQuestions = questions.length;

    return (
        <div className="wg-assistant-shell">
            <div className="wg-assistant-mobile-status">
                <div className="wg-assistant-mobile-status-top">
                    <div>
                        <span>AI Builder</span>
                        <strong>
                            {isLast ? "Plan ready" : `Question ${index + 1} of ${totalQuestions}`}
                        </strong>
                    </div>

                    <div className="wg-assistant-mobile-score">
                        {progress}%
                    </div>
                </div>

                <div className="wg-assistant-mobile-dots">
                    {questions.map((question, dotIndex) => (
                        <span
                            key={question.key}
                            className={
                                dotIndex < index
                                    ? "done"
                                    : dotIndex === index
                                        ? "active"
                                        : ""
                            }
                        />
                    ))}
                </div>

                <div className="wg-assistant-mobile-mini">
                    <span>{completedCount} completed</span>
                    <span>{totalQuestions - index - 1} remaining</span>
                </div>
            </div>
            <div className="wg-assistant-hero">
                <div>
                    <div className="wg-badge">
                        <Sparkles size={16} />
                        <span>AI Assistant Mode</span>
                    </div>

                    <h2>Build your website with guided AI questions</h2>

                    <p>
                        Answer a few simple questions. Nexus will prepare the right website type,
                        pages, CRM modules, visual direction, and generation payload.
                    </p>
                </div>

                <button
                    type="button"
                    className="wg-btn ghost"
                    onClick={onUseManual}
                >
                    Use manual form
                </button>
            </div>

            <div className="wg-assistant-progress">
                <span style={{ width: `${progress}%` }} />
            </div>

            <div className="wg-assistant-question-wrap">
                {current.render()}
            </div>

            <div className="wg-assistant-actions">
                <button
                    type="button"
                    className="wg-btn secondary"
                    onClick={() => setIndex((prev) => Math.max(0, prev - 1))}
                    disabled={index === 0}
                >
                    <ArrowLeft size={16} />
                    Back
                </button>

                {!isLast ? (
                    <button
                        type="button"
                        className="wg-btn primary"
                        onClick={() => setIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                        disabled={!current.isValid()}
                    >
                        Continue
                        <ArrowRight size={16} />
                    </button>
                ) : (
                    <button
                        type="button"
                        className="wg-btn primary"
                        onClick={applyFinalPlan}
                        disabled={autofilling}
                    >
                        {autofilling ? "Preparing AI plan..." : "Use this plan"}
                        <Wand2 size={16} />
                    </button>
                )}
            </div>
        </div>
    );
}